import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Mail, 
  Search, 
  Eye,
  Trash2,
  Phone,
  Plus,
  Pencil,
  Columns,
  X,
  Table2
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../../config';

const DEFAULT_BOARD_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'vehicle', label: 'Vehicle' },
  { key: 'country', label: 'Country' },
  { key: 'source', label: 'Source' },
  { key: 'status', label: 'Status' },
  { key: 'agent', label: 'Agent' },
  { key: 'notes', label: 'Notes' }
];

const BOARD_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'CONTACTED', label: 'Contacted' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' }
];

const boardStatusStyle = (value) => {
  switch ((value || '').toUpperCase()) {
    case 'CONTACTED':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'COMPLETED':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'CANCELLED':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'PENDING':
    default:
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  }
};

const slugifyColumnKey = (label) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `col_${Date.now()}`;

const ensureBoardColumns = (columns) => {
  const cols = (columns || []).map(c => ({ ...c }));
  const has = (k) => cols.some(c => c.key === k);
  const insertAt = (col) => {
    const notesIdx = cols.findIndex(c => c.key === 'notes');
    if (notesIdx === -1) cols.push(col);
    else cols.splice(notesIdx, 0, col);
  };
  if (!has('status')) insertAt({ key: 'status', label: 'Status' });
  if (!has('agent')) insertAt({ key: 'agent', label: 'Agent' });
  return cols;
};

const BOARD_COLUMN_WIDTHS = {
  name: 180,
  email: 220,
  phone: 150,
  vehicle: 230,
  country: 130,
  source: 150,
  status: 150,
  agent: 180,
  notes: 260
};

const boardColumnWidth = (key) => BOARD_COLUMN_WIDTHS[key] || 160;

const CustomerInquiries = ({ user }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const [activeTab, setActiveTab] = useState('inquiries');
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [showNewBoardDialog, setShowNewBoardDialog] = useState(false);
  const [showEditBoardDialog, setShowEditBoardDialog] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardColumns, setNewBoardColumns] = useState(DEFAULT_BOARD_COLUMNS.map(c => ({ ...c })));
  const [editBoardName, setEditBoardName] = useState('');
  const [editBoardColumns, setEditBoardColumns] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchInquiries();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (activeTab === 'boards' && boards.length === 0 && !boardsLoading) {
      fetchBoards();
    }
  }, [activeTab]);

  useEffect(() => {
    if (boards.length > 0 && !boards.find(b => b.id === selectedBoardId)) {
      setSelectedBoardId(boards[0].id);
    } else if (boards.length === 0) {
      setSelectedBoardId(null);
    }
  }, [boards]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data);
    } catch (error) {
      toast.error('Failed to fetch inquiries');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/inquiries/${inquiryId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Inquiry status updated');
      fetchInquiries();
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry({ ...selectedInquiry, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
      console.error('Error:', error);
    }
  };

  const deleteInquiry = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/inquiries/${inquiryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inquiry deleted');
      fetchInquiries();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error('Failed to delete inquiry');
      console.error('Error:', error);
    }
  };

  const filteredInquiries = inquiries.filter(inquiry => {
    const matchesSearch = 
      inquiry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.vehicle_model?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || inquiry.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      contacted: { color: 'bg-blue-100 text-blue-800', label: 'Contacted' },
      resolved: { color: 'bg-green-100 text-green-800', label: 'Resolved' },
      closed: { color: 'bg-gray-100 text-gray-800', label: 'Closed' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const fetchBoards = async () => {
    try {
      setBoardsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inquiries/boards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoards(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch inquiry boards');
      console.error('Error:', error);
    } finally {
      setBoardsLoading(false);
    }
  };

  const selectedBoard = boards.find(b => b.id === selectedBoardId) || null;

  const openNewBoardDialog = () => {
    setNewBoardName('');
    setNewBoardColumns(DEFAULT_BOARD_COLUMNS.map(c => ({ ...c })));
    setShowNewBoardDialog(true);
  };

  const setBoardColumnLabel = (setter, index, label) => {
    setter(prev => prev.map((c, i) => (i === index ? { ...c, label, key: slugifyColumnKey(label) } : c)));
  };

  const addBoardColumn = (setter) => {
    setter(prev => [...prev, { key: `col_${Date.now()}`, label: '' }]);
  };

  const removeBoardColumn = (setter, index) => {
    setter(prev => prev.filter((_, i) => i !== index));
  };

  const createBoard = async () => {
    if (!newBoardName.trim()) {
      toast.error('Board name is required');
      return;
    }
    const columns = newBoardColumns.filter(c => c.label.trim()).map(c => ({ key: c.key, label: c.label.trim() }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/inquiries/boards`, { name: newBoardName.trim(), columns }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoards(prev => [...prev, response.data]);
      setSelectedBoardId(response.data.id);
      setShowNewBoardDialog(false);
      toast.success('Board created');
    } catch (error) {
      toast.error('Failed to create board');
      console.error('Error:', error);
    }
  };

  const openEditBoardDialog = () => {
    if (!selectedBoard) return;
    setEditBoardName(selectedBoard.name);
    setEditBoardColumns(ensureBoardColumns(selectedBoard.columns));
    setShowEditBoardDialog(true);
  };

  const saveBoard = async () => {
    if (!editBoardName.trim()) {
      toast.error('Board name is required');
      return;
    }
    const columns = editBoardColumns.filter(c => c.label.trim()).map(c => ({ key: c.key, label: c.label.trim() }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API}/inquiries/boards/${selectedBoardId}`, { name: editBoardName.trim(), columns }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoards(prev => prev.map(b => (b.id === selectedBoardId ? { ...b, ...response.data } : b)));
      setShowEditBoardDialog(false);
      toast.success('Board updated');
    } catch (error) {
      toast.error('Failed to update board');
      console.error('Error:', error);
    }
  };

  const deleteBoard = async () => {
    if (!selectedBoard) return;
    if (!window.confirm(`Are you sure you want to delete board "${selectedBoard.name}" and all its rows?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/inquiries/boards/${selectedBoardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoards(prev => prev.filter(b => b.id !== selectedBoardId));
      setSelectedBoardId(null);
      toast.success('Board deleted');
    } catch (error) {
      toast.error('Failed to delete board');
      console.error('Error:', error);
    }
  };

  const addRow = async () => {
    if (!selectedBoard) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/inquiries/boards/${selectedBoardId}/rows`, { cells: { status: 'PENDING' } }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoards(prev => prev.map(b => (b.id === selectedBoardId ? { ...b, rows: [...(b.rows || []), response.data] } : b)));
    } catch (error) {
      toast.error('Failed to add row');
      console.error('Error:', error);
    }
  };

  const startEditingCell = (rowId, colKey, value) => {
    setEditingCell({ boardId: selectedBoardId, rowId, colKey, value: value || '' });
  };

  const commitCell = async () => {
    if (!editingCell) return;
    const { boardId, rowId, colKey, value } = editingCell;
    setEditingCell(null);
    const board = boards.find(b => b.id === boardId);
    if (!board) return;
    const target = (board.rows || []).find(r => r.id === rowId);
    if (!target) return;
    const cells = { ...(target.cells || {}), [colKey]: value };
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API}/inquiries/boards/${boardId}/rows/${rowId}`, { cells }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoards(prev => prev.map(b => (b.id === boardId ? response.data : b)));
    } catch (error) {
      toast.error('Failed to save cell');
      console.error('Error:', error);
    }
  };

  const deleteRow = async (rowId) => {
    if (!window.confirm('Delete this row?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/inquiries/boards/${selectedBoardId}/rows/${rowId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoards(prev => prev.map(b => (b.id === selectedBoardId ? { ...b, rows: (b.rows || []).filter(r => r.id !== rowId) } : b)));
      toast.success('Row deleted');
    } catch (error) {
      toast.error('Failed to delete row');
      console.error('Error:', error);
    }
  };

  const updateBoardCell = async (rowId, colKey, value) => {
    const board = boards.find(b => b.id === selectedBoardId);
    if (!board) return;
    const target = (board.rows || []).find(r => r.id === rowId);
    if (!target) return;
    const cells = { ...(target.cells || {}), [colKey]: value };
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API}/inquiries/boards/${selectedBoardId}/rows/${rowId}`, { cells }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoards(prev => prev.map(b => (b.id === selectedBoardId ? response.data : b)));
    } catch (error) {
      toast.error('Failed to save');
      console.error('Error:', error);
    }
  };

  const exportBoard = async (format) => {
    if (!selectedBoard) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/inquiries/boards/${selectedBoardId}/export`, {
        params: { format },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (selectedBoard.name || 'board').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'board';
      link.setAttribute('download', `${safeName}_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export board');
      console.error('Error:', error);
    }
  };

  const selectedBoardColumns = selectedBoard ? ensureBoardColumns(selectedBoard.columns) : [];


  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customer Inquiries</h1>
          <p className="text-gray-500 mt-1">Manage inquiries from the public website and external boards</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="inquiries">Website Inquiries</TabsTrigger>
          <TabsTrigger value="boards">Boards</TabsTrigger>
        </TabsList>

        <TabsContent value="inquiries" className="space-y-6 mt-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search by name, email, or vehicle..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Inquiries Table */}
          <Card>
            <CardHeader>
              <CardTitle>Inquiries ({filteredInquiries.length})</CardTitle>
              <CardDescription>View and manage customer inquiries</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading inquiries...</div>
              ) : filteredInquiries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>No inquiries found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInquiries.map((inquiry) => (
                      <TableRow key={inquiry.id}>
                        <TableCell className="font-medium">{inquiry.name}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {inquiry.email}
                            </div>
                            {inquiry.phone && (
                              <div className="flex items-center gap-1 text-gray-500">
                                <Phone className="h-3 w-3" />
                                {inquiry.phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {inquiry.vehicle_model ? (
                            <div className="text-sm">
                              <div className="font-medium">{inquiry.vehicle_model}</div>
                              {inquiry.vehicle_stock_no && (
                                <div className="text-gray-500">#{inquiry.vehicle_stock_no}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">General Inquiry</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{inquiry.message}</TableCell>
                        <TableCell>{getStatusBadge(inquiry.status)}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(inquiry.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedInquiry(inquiry);
                                setShowDetailDialog(true);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="boards" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div>
                  <CardTitle>External Inquiry Boards</CardTitle>
                  <CardDescription>Spreadsheet-style boards for inquiries received outside the website</CardDescription>
                </div>
                <Button onClick={openNewBoardDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Board
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {boardsLoading ? (
                <div className="text-center py-8">Loading boards...</div>
              ) : boards.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Columns className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>No boards yet. Create a board to track outside inquiries.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="w-72">
                      <Label className="text-sm font-medium">Board</Label>
                      <Select value={selectedBoardId || ''} onValueChange={setSelectedBoardId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select board" />
                        </SelectTrigger>
                        <SelectContent>
                          {boards.map(b => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="outline" onClick={openEditBoardDialog} disabled={!selectedBoard}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button variant="destructive" onClick={deleteBoard} disabled={!selectedBoard}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                    <Button variant="outline" onClick={() => exportBoard('csv')} disabled={!selectedBoard}>
                      <Download className="h-4 w-4 mr-2" />
                      CSV
                    </Button>
                    <Button variant="outline" onClick={() => exportBoard('xlsx')} disabled={!selectedBoard}>
                      <Download className="h-4 w-4 mr-2" />
                      Excel
                    </Button>
                    <div className="flex-1" />
                    <Button onClick={addRow} disabled={!selectedBoard}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Row
                    </Button>
                  </div>

                  {selectedBoard ? (
                    <Card className="border-gray-200 shadow-sm overflow-hidden">
                      <div className="w-full max-h-[68vh] overflow-auto rounded-lg border border-gray-200 bg-white [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-track]:bg-gray-50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr>
                              {selectedBoardColumns.map((col, idx) => (
                                <th
                                  key={col.key}
                                  style={{ minWidth: boardColumnWidth(col.key) }}
                                  className={`sticky top-0 z-20 h-11 whitespace-nowrap border-b border-gray-200 bg-gray-50 px-3 text-left align-middle text-[11px] font-semibold uppercase tracking-wider text-gray-500 ${idx === 0 ? 'left-0 z-30 border-r border-gray-200' : ''}`}
                                >
                                  {col.label}
                                </th>
                              ))}
                              <th className="sticky right-0 top-0 z-30 h-11 w-14 border-b border-l border-gray-200 bg-gray-50 px-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedBoard.rows || []).length === 0 ? (
                              <tr>
                                <td colSpan={selectedBoardColumns.length + 1} className="py-16 text-center text-gray-500">
                                  <Table2 className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                                  <p className="text-sm">No rows yet</p>
                                  <p className="mt-1 text-xs text-gray-400">Click "Add Row" to start entering inquiries.</p>
                                </td>
                              </tr>
                            ) : (
                              (selectedBoard.rows || []).map(row => (
                                <tr key={row.id} className="group border-b border-gray-100 transition-colors last:border-0 hover:bg-gray-50">
                                  {selectedBoardColumns.map((col, idx) => {
                                    const isEditing =
                                      editingCell && editingCell.rowId === row.id && editingCell.colKey === col.key;
                                    const value = (row.cells || {})[col.key] || '';
                                    const stickyCls = idx === 0
                                      ? 'sticky left-0 z-10 border-r border-gray-200 bg-white group-hover:bg-gray-50'
                                      : '';
                                    if (col.key === 'status') {
                                      return (
                                        <td key={col.key} className="p-1.5 align-middle" style={{ minWidth: boardColumnWidth(col.key) }}>
                                          <Select
                                            value={(value || 'PENDING').toUpperCase()}
                                            onValueChange={(v) => updateBoardCell(row.id, col.key, v)}
                                          >
                                            <SelectTrigger className={`h-8 text-xs font-medium ${boardStatusStyle(value)}`}>
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {BOARD_STATUS_OPTIONS.map(opt => (
                                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </td>
                                      );
                                    }
                                    if (col.key === 'agent') {
                                      return (
                                        <td key={col.key} className="p-1.5 align-middle" style={{ minWidth: boardColumnWidth(col.key) }}>
                                          <Select
                                            value={value || 'unassigned'}
                                            onValueChange={(val) => updateBoardCell(row.id, col.key, val === 'unassigned' ? '' : val)}
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue placeholder="Unassigned" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="unassigned">Unassigned</SelectItem>
                                              {employees.map(emp => (
                                                <SelectItem key={emp.id} value={emp.id}>{emp.full_name || emp.email}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </td>
                                      );
                                    }
                                    if (isEditing) {
                                      return (
                                        <td key={col.key} className={`p-1.5 align-middle ${stickyCls}`} style={{ minWidth: boardColumnWidth(col.key) }}>
                                          <Input
                                            autoFocus
                                            value={editingCell.value}
                                            onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                                            onBlur={commitCell}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') commitCell();
                                              if (e.key === 'Escape') e.currentTarget.blur();
                                            }}
                                            className="h-8"
                                          />
                                        </td>
                                      );
                                    }
                                    return (
                                      <td key={col.key} className={`p-1.5 align-middle ${stickyCls}`} style={{ minWidth: boardColumnWidth(col.key), maxWidth: boardColumnWidth(col.key) }}>
                                        <button
                                          type="button"
                                          onClick={() => startEditingCell(row.id, col.key, value)}
                                          title={value || ''}
                                          className={`block w-full truncate rounded border border-transparent px-2 py-1.5 text-left text-sm transition-colors hover:border-gray-300 hover:bg-white ${value ? 'text-gray-900' : 'text-gray-300'}`}
                                        >
                                          {value || '—'}
                                        </button>
                                      </td>
                                    );
                                  })}
                                  <td className="sticky right-0 z-10 w-14 border-l border-gray-100 bg-white px-1 align-middle group-hover:bg-gray-50">
                                    <div className="flex justify-center">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                                        onClick={() => deleteRow(row.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      Select a board above to view its rows.
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Inquiry Details</DialogTitle>
          </DialogHeader>
          {selectedInquiry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Customer Name</Label>
                  <p className="text-sm mt-1">{selectedInquiry.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Email</Label>
                  <p className="text-sm mt-1">{selectedInquiry.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Phone</Label>
                  <p className="text-sm mt-1">{selectedInquiry.phone || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Country</Label>
                  <p className="text-sm mt-1">{selectedInquiry.country || 'N/A'}</p>
                </div>
              </div>

              {selectedInquiry.vehicle_model && (
                <div>
                  <Label className="text-sm font-medium">Vehicle Interest</Label>
                  <div className="mt-1 p-3 bg-gray-50 rounded">
                    <p className="font-medium">{selectedInquiry.vehicle_model}</p>
                    {selectedInquiry.vehicle_stock_no && (
                      <p className="text-sm text-gray-500">Stock #: {selectedInquiry.vehicle_stock_no}</p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium">Message</Label>
                <div className="mt-1 p-3 bg-gray-50 rounded">
                  <p className="text-sm whitespace-pre-wrap">{selectedInquiry.message}</p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Status</Label>
                <Select 
                  value={selectedInquiry.status} 
                  onValueChange={(value) => updateInquiryStatus(selectedInquiry.id, value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <Button
                  variant="destructive"
                  onClick={() => deleteInquiry(selectedInquiry.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Inquiry
                </Button>
                <Button onClick={() => setShowDetailDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Board Dialog */}
      <Dialog open={showNewBoardDialog} onOpenChange={setShowNewBoardDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Inquiry Board</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Board Name</Label>
              <Input
                value={newBoardName}
                onChange={(e) => setNewBoardName(e.target.value)}
                placeholder="e.g. WhatsApp Inquiries"
                className="mt-1"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-sm font-medium">Columns</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => addBoardColumn(setNewBoardColumns)}>
                  <Plus className="h-3 w-3 mr-1" />
                  Column
                </Button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {newBoardColumns.map((col, index) => (
                  <div key={col.key} className="flex gap-2 items-center">
                    <Input
                      value={col.label}
                      onChange={(e) => setBoardColumnLabel(setNewBoardColumns, index, e.target.value)}
                      placeholder="Column label (auto key)"
                      className="flex-1"
                    />
                    {newBoardColumns.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBoardColumn(setNewBoardColumns, index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowNewBoardDialog(false)}>
                Cancel
              </Button>
              <Button onClick={createBoard}>
                Create Board
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Board Dialog */}
      <Dialog open={showEditBoardDialog} onOpenChange={setShowEditBoardDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Inquiry Board</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Board Name</Label>
              <Input
                value={editBoardName}
                onChange={(e) => setEditBoardName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-sm font-medium">Columns</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => addBoardColumn(setEditBoardColumns)}>
                  <Plus className="h-3 w-3 mr-1" />
                  Column
                </Button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {editBoardColumns.map((col, index) => (
                  <div key={col.key} className="flex gap-2 items-center">
                    <Input
                      value={col.label}
                      onChange={(e) => setBoardColumnLabel(setEditBoardColumns, index, e.target.value)}
                      className="flex-1"
                    />
                    {editBoardColumns.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBoardColumn(setEditBoardColumns, index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Changing column labels does not delete existing row data.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowEditBoardDialog(false)}>
                Cancel
              </Button>
              <Button onClick={saveBoard}>
                Save Board
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerInquiries;